<template>
  <div>
    <div v-if="filteredListUpdated"
         :key="filteredListUpdated"
         data-test="filter-updated-announcement"
         class="sr-only"
         aria-live="polite"
    >
      {{ t('accessibility.filter_list_updated', { type: selectedTypeText }) }}
    </div>
    <h2 v-if="title" data-test="choose-channels-title">{{ title }}</h2>
    <div class="channel-selection mb-4"
         data-test="channel-selection"
         :class="[!localEntities.length ? 'no-shadow' : '', { 'mb-6': modelValue?.length }]"
    >
      <sdui-waterfall-search v-model="channels"
                             class="p-0 custom-search"
                             :url="url"
      >
        <template #quick-actions>
          <div class="mt-n3">
            <STabHeader
              v-if="channelTypes.length > 1"
              data-test="channel-tab-header"
              class="flex-wrap mb-4"
              :tablist-label="t('ui.navigation')"
              :tabs="channelTypes"
              :default-active-tab-index="defaultActiveTabIndex"
              @change="filteredListUpdated = new Date().getTime()"
            />
          </div>
        </template>
        <SListItem
          v-for="(group, i) in localEntities"
          :key="group.id"
          :title="group.meta.displayname"
          :data-test="`channel-list-item-${group.id}`"
          :data-cy="`list-item-${group.target}`"
          :icon-text="['courses', 'course-parents'].includes(group.target) ? group.meta.shortcut : null"
          :icon="options[i].icon"
          :icon-color="group.is_disabled ? 'gray-300' : options[i].color"
          :badges="
            group.is_disabled
              ? [
                {
                  text: group.meta.subtitle,
                  color: 'secondary',
                },
              ]
              : []
          "
          :description="description(group)"
          :aria-pressed="selected[group.id + group.type] ? 'true' : 'false'"
          right-action-icon="icon-chevron-right"
          truncate-title
          truncate-description
          @click.prevent="itemsSelectable ? toggleChannel(group) : emit('select-channel', group)"
        >
          <template #right-action>
            <SCheckbox
              v-if="itemsSelectable"
              v-model="selected[group.id + group.type]"
              :data-test="`select-entity-${group.id}`"
              name="select-entity"
              :label-text="t('ui.choose_channels_dialog.add')"
              checkbox-type="btn"
            />
          </template>
        </SListItem>
      </sdui-waterfall-search>
    </div>

    <div v-if="channelFooter"
         data-test="choose-channels-footer"
         :class="{ 'fixed-bottom fixed-with-shadow': !hasFixedBottomButton }"
    >
      <component :is="hasFixedBottomButton ? SModalPortalTo : 'div'">
        <div class="container p-0"
             data-test="choose-channels-footer-container"
             :aria-hidden="modelValue?.length ? 'false' : 'true'"
        >
          <h2 class="sr-only">{{ t('news.create.choose_channels.active_channels') }}</h2>
          <ul v-if="modelValue?.length"
              data-test="selected-channels-list"
              class="list-unstyled mb-0 selected-users bottom-footer"
          >
            <li v-for="(channel, i) in modelValue ?? []"
                :key="channel.id"
                :data-test="`selected-channel-${channel.id}`"
                class="mx-2 selected-item"
            >
              <b-badge class="btn-round-padding with-icon pr-2">
                <sdui-icon
                  :class="[getProps[i].color, getProps[i].color + '-light-bg', !getProps[i].icon ? 'circle text' : getProps[i].icon]"
                  class="mr-1 aux-height-width-border-radius justify-content-center align-items-center d-flex"
                >
                  <span v-if="!getProps[i].icon">{{ channel.meta.shortcut }}</span>
                </sdui-icon>
                <span class="username">{{ channel.meta.displayname }}</span>
              </b-badge>
              <b-button variant="transparent"
                        :data-test="`remove-channel-${channel.id}`"
                        class="remove-user icon sdui-icon reset-button btn-circle"
                        @click="toggleChannel(channel)"
              >
                <sdui-icon class="icon-close-o-16" />
                <span class="sr-only">{{ t('ui.user.remove', { name: channel.meta.displayname }) }}</span>
              </b-button>
            </li>
          </ul>
        </div>
        <slot name="submit" />
      </component>
    </div>
  </div>
</template>

<script lang="ts">
// TODO VUE3: Remove compatConfig when @vue/compat is removed.
// Keeps Vue 3 v-model semantics (modelValue/update:modelValue) under @vue/compat global mode.
export default {
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },
}
</script>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEntity } from '@/composables/utils/useEntity.ts'
import { useCurrentUser } from '@/composables/useCurrentUser.ts'
import { Channel, User } from 'src/store/models/index.js'
import SduiWaterfallSearch from 'src/components/miscellaneous/ui/common/SduiWaterfallSearch.vue'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'
import SCheckbox from 'src/components/base/SCheckbox.vue'
import STabHeader from 'src/components/base/STabHeader.vue'
import type { MessageForwardRecipient } from '@/types/chat/MessageForward.ts'
import type { Channel as ChannelData, ChannelMeta } from '@/types/store/models/channel.ts'

interface ChannelSearchResult {
  type: 'Channel' | 'User'
  entity: Record<string, unknown>
}

type ChannelListEntity = Pick<ChannelData, 'id' | 'group' | 'is_disabled'> & {
  target: string
  type: string
  meta: Pick<ChannelMeta, 'displayname' | 'shortcut' | 'subtitle'>
}

const props = withDefaults(
  defineProps<{
    modelValue?: ChannelListEntity[]
    baseUrl: string
    channelFooter?: boolean
    title?: string
    isAdminGroupList?: boolean // To process different logic/design on news creation and management channels preview
    itemsSelectable?: boolean
    showDescription?: boolean
    hasFixedBottomButton?: boolean // to allow channel selection inside modal with fixed bottom
  }>(),
  {
    modelValue: () => [],
    channelFooter: false,
    title: '',
    isAdminGroupList: false,
    itemsSelectable: false,
    showDescription: false,
    hasFixedBottomButton: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: ChannelListEntity[]): void
  (e: 'select-channel', group: MessageForwardRecipient): void
}>()

const route = useRoute()
const { t } = useI18n()
const { me } = useCurrentUser()
const { getEntityData } = useEntity()

const channels = ref<ChannelSearchResult[]>([])
const localEntities = ref<ChannelListEntity[]>([])
const filteredListUpdated = ref<number | undefined>(undefined)

interface ChannelTypeSetting {
  text: string
  id: string
  hideInAdminGroupList?: boolean
}

const selectedType = computed(() => route.query.activeTab)

const newsChannelTypes = computed(() => {
  const types = me.value.school.meta.news_channel_types
  if (types && types.length) return types
  return ['global', 'individual', 'grades', 'courses', 'users']
})

const channelTypesSettings = computed<Record<string, ChannelTypeSetting>>(() => ({
  global: {
    text: t('news.channel_types.global'),
    id: 'global',
  },

  individual: {
    text: t('news.channel_types.custom'),
    id: 'custom',
    hideInAdminGroupList: true,
  },

  grades: {
    text: t('news.channel_types.generated'),
    id: 'students|teachers|parents&type=generated',
  },

  courses: {
    text: t('news.channel_types.courses'),
    id: 'courses|course-parents',
  },

  users: {
    text: t('news.channel_types.users'),
    id: 'users',
    hideInAdminGroupList: true,
  },
}))

const channelTypes = computed(() =>
  newsChannelTypes.value
    .map((key) => channelTypesSettings.value[key])
    .filter((type) => (props.isAdminGroupList ? !type.hideInAdminGroupList : true)),
)

const defaultActiveTabIndex = computed(() => {
  const customTab = channelTypes.value.find((type) => type.id === 'custom')
  return !props.isAdminGroupList && customTab ? channelTypes.value.indexOf(customTab) : 0
})

const selectedTypeText = computed(
  () => channelTypes.value.find((type) => type.id === selectedType.value)?.text,
)

// Each item is typed as ChannelListEntity, but is actually a real Channel or User
// instance at runtime. Cast it back here because getEntityData checks the real class
// (via `instanceof`) to decide what to return.
// TODO VUE3: drop this cast once vuex-orm models are properly typed
const options = computed(() =>
  localEntities.value.map((s) => getEntityData(s as unknown as InstanceType<typeof Channel> | InstanceType<typeof User>)),
)

const getProps = computed(() =>
  props.modelValue.map((s) => getEntityData(s as unknown as InstanceType<typeof Channel> | InstanceType<typeof User>)),
)

const selected = computed(() =>
  props.modelValue.reduce<Record<string, boolean>>((acc, c) => ({ ...acc, [c.id + c.type]: true }), {}),
)

const url = computed(() => {
  const defaultTab = channelTypes.value[defaultActiveTabIndex.value]
  const paramsEntity = selectedType.value || defaultTab?.id || channelTypes.value[0]?.id
  const paramsType = paramsEntity === 'global' || paramsEntity === 'custom' ? 'type' : 'target'

  if (!paramsEntity) return `${props.baseUrl}=students|teachers|parents&`
  return `${props.baseUrl}${paramsType}=${paramsEntity}&order-by=name&order-dir=asc&`
})

const emitChange = (channel: ChannelListEntity) => {
  const checkGlobal = (c: ChannelListEntity) => c.type === 'GLOBAL' && c.group === 'global' && c.target === 'global'
  const isSelected =
    props.modelValue.findIndex((c) => c.id === channel.id && c.type === channel.type) !== -1
  if (isSelected) return props.modelValue.filter((el) => el.id !== channel.id)
  if (checkGlobal(channel)) return [channel]
  return [channel, ...props.modelValue.filter((c) => !checkGlobal(c))]
}

const toggleChannel = (channel: ChannelListEntity) => {
  emit('update:modelValue', emitChange(channel))
}

const description = ({ is_disabled: disabled, meta: { subtitle } }: ChannelListEntity) => {
  if (disabled) return '' // for disabled channels, subtitle will be displayed as badge
  if (props.showDescription) return subtitle
  return ''
}

watch(
  channels,
  (val) => {
    if (!val) return

    const channelResults = val.filter((el) => el.type === 'Channel')
    const userResults = val.filter((el) => el.type === 'User')
    // Store real Channel/User instances but expose them as the display shape.
    // getEntityData casts back to the real class at the point of use (see `options`).
    // TODO VUE3: drop these casts once vuex-orm models are properly typed
    localEntities.value = [
      ...channelResults.map((el) => new Channel(el.entity) as unknown as ChannelListEntity),
      ...userResults.map((el) => new User(el.entity) as unknown as ChannelListEntity),
    ]
  },
  { deep: true },
)

watch(url, () => {
  channels.value = []
})
</script>

<style scoped lang="scss">
.aux-height-width-border-radius {
  height: 34px !important;
  width: 34px !important;
  border-radius: 50%;
}

:deep(.s-tab-button) {
  border-bottom: $color-gray-100 1px solid;
  border-radius: 0;
  min-width: 120px;
  min-height: 60px !important;
}

.bottom-footer {
  overflow-x: auto;
  align-items: center;
  display: flex;
  min-height: 80px;
  margin-left: -10px;

  @include media($mobile) {
    margin-left: 0;
    padding-left: 5px;
  }
}

a.icon:hover {
  color: $color-white !important;
}

.circle.text {
  font-style: initial;
}

.form-group {
  margin-bottom: 0px;
  margin-top: 0px;
}
</style>
